import type { FC, ReactNode } from 'react';
import { FileText, CheckCircle2, PenLine } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Loader } from '@/components/ui';
import { useStrategicPillarStats } from '@/lib/api/hooks/strategic-pillar';

interface StatsCardProps {
	title: string;
	value: number;
	description: string;
	icon: ReactNode;
	iconContainerClassName: string;
	iconClassName: string;
}

type StatKey = 'total' | 'totalPublished' | 'totalDrafts';

interface StatConfig {
	key: StatKey;
	title: string;
	icon: LucideIcon;
	iconContainerClassName: string;
	iconClassName: string;
	getDescription: (values: Record<StatKey, number>) => string;
}

const StatCard = ({
	title,
	value,
	description,
	icon,
	iconContainerClassName,
	iconClassName,
}: StatsCardProps) => {
	return (
		<article className="rounded-xl border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="grid grid-cols-[auto_1fr] gap-4">
				<div className={`flex h-12 w-12 items-center justify-center rounded ${iconContainerClassName}`}>
					<span className={iconClassName}>{icon}</span>
				</div>
				<div>
					<p className="text-base font-medium text-gray-600">{title}</p>
					<p className="text-[2rem] leading-[1.1] font-semibold text-gray-900">{value}</p>
					<p className="text-sm text-gray-500">{description}</p>
				</div>
			</div>
		</article>
	);
};

const calculatePercent = (value: number, total: number): string => {
	if (!total) return '0.0%';
	return `${((value / total) * 100).toFixed(1)}%`;
};

const STATS_CONFIG: StatConfig[] = [
	{
		key: 'total',
		title: 'Total Strategic Pillars',
		icon: FileText,
		iconContainerClassName: 'bg-[#F8E8EB70]',
		iconClassName: 'text-[#8F2F44]',
		getDescription: () => 'All time',
	},
	{
		key: 'totalPublished',
		title: 'Published',
		icon: CheckCircle2,
		iconContainerClassName: 'bg-[#E8F6EE70]',
		iconClassName: 'text-[#0F8A47]',
		getDescription: ({ totalPublished, total }) => `${calculatePercent(totalPublished, total)} of total`,
	},
	{
		key: 'totalDrafts',
		title: 'Drafts',
		icon: PenLine,
		iconContainerClassName: 'bg-[#FDF3E670]',
		iconClassName: 'text-[#B86A00]',
		getDescription: ({ totalDrafts, total }) => `${calculatePercent(totalDrafts, total)} of total`,
	},
];

const StrategicPillarsStats: FC = () => {
	const { data: stats, isFetching, refetch, isError } = useStrategicPillarStats();

	const values: Record<StatKey, number> = {
		total: stats?.total ?? 0,
		totalPublished: stats?.totalPublished ?? 0,
		totalDrafts: stats?.totalDrafts ?? 0,
	};

	return (
		<section className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:flex ">
			{isError ? (
				<div className="p-4 flex flex-col items-center justify-center h-full w-full gap-4">
					<p>Error loading statistics.</p>
					<button onClick={() => refetch()} className="px-4 py-2 bg-forestGreen-100 text-white rounded">Retry</button>
				</div>
			) : isFetching ? (
				STATS_CONFIG.map((stat) => {
					return (
						<div key={stat.key} className="p-4 flex items-center justify-center h-full min-h-28 w-full">
							<Loader size="md" variant="wine" />
						</div>
					);
				})
			) : (
				STATS_CONFIG.map((stat) => {
					const Icon = stat.icon;

					return (
						<StatCard
							key={stat.key}
							title={stat.title}
							value={values[stat.key]}
							description={stat.getDescription(values)}
							icon={<Icon className="h-4 w-4 md:h-6 md:w-6"  />}
							iconContainerClassName={stat.iconContainerClassName}
							iconClassName={stat.iconClassName}
						/>
					);
				})
			)}
		</section>
	);
};

export default StrategicPillarsStats;
